/**
 * VerticalIconTabs — a slim vertical rail of icon-only tabs.
 *
 * Used for the run detail inspector, which sits under the video and tabs on its
 * left edge. Icon-only with a hover tooltip (label + count) and a right-edge
 * accent bar on the active tab so it reads as connected to the panel on its
 * right. The horizontal `UnderlineTabs` is the wrong fit here (this is a
 * left-edge, vertical, icon-driven rail), so this is a purpose-built variant.
 *
 * @module ui/pages/RunDetailPage/VerticalIconTabs
 */

import React from 'react';
import { Tooltip, TooltipTrigger } from '@chatsdet/ui';

export interface VerticalIconTabItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  /** Optional count shown as a small badge + in the tooltip. */
  count?: number;
}

export interface VerticalIconTabsProps {
  items: VerticalIconTabItem[];
  selectedId: string;
  onSelect: (id: string) => void;
  className?: string;
}

export const VerticalIconTabs: React.FC<VerticalIconTabsProps> = ({
  items,
  selectedId,
  onSelect,
  className,
}) => {
  return (
    <div
      role="tablist"
      aria-orientation="vertical"
      className={`flex w-12 shrink-0 flex-col border-r border-border bg-card ${className ?? ''}`}
    >
      {items.map((item) => {
        const active = item.id === selectedId;
        return (
          <TooltipTrigger key={item.id} delay={300} placement="right">
            <button
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={item.label}
              onClick={() => onSelect(item.id)}
              className={`relative flex h-12 w-full items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                active
                  ? 'text-primary'
                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              }`}
            >
              {/* Active accent — left edge, connecting the rail to the panel */}
              {active && (
                <span className="absolute inset-y-2 left-0 w-0.5 rounded-r-full bg-primary" />
              )}
              <span className="relative">
                {item.icon}
                {item.count !== undefined && item.count > 0 && (
                  <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none text-primary-foreground">
                    {item.count > 99 ? '99+' : item.count}
                  </span>
                )}
              </span>
            </button>
            <Tooltip>
              {item.label}
              {item.count !== undefined && item.count > 0 ? ` (${item.count})` : ''}
            </Tooltip>
          </TooltipTrigger>
        );
      })}
    </div>
  );
};
